import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { BarChart3, Bot, FolderPlus, Plus, Settings, Workflow } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

const { Text } = Typography;

export interface QuickActionItem {
  id: string;
  label: string;
  subtitle: string;
  color: string;
  background: string;
  icon: LucideIcon;
  onClick?: () => void;
}

export interface QuickActionsWidgetProps {
  actions?: readonly QuickActionItem[];
  className?: string;
  testId?: string;
}

export function QuickActionsWidget({
  actions,
  className,
  testId,
}: QuickActionsWidgetProps): ReactElement {
  const { t } = useTranslation();
  const fallbackActions: readonly QuickActionItem[] = [
    {
      id: 'new-project',
      label: t('crm.dashboard.actions.new_project.label'),
      subtitle: t('crm.dashboard.actions.new_project.subtitle'),
      color: 'var(--color-brand-500)',
      background: 'var(--ant-color-primary-bg)',
      icon: FolderPlus,
    },
    {
      id: 'summon-agent',
      label: t('crm.dashboard.actions.summon_agent.label'),
      subtitle: t('crm.dashboard.actions.summon_agent.subtitle'),
      color: 'var(--ant-color-info)',
      background: 'var(--ant-color-info-bg)',
      icon: Bot,
    },
    {
      id: 'new-pipeline',
      label: t('crm.dashboard.actions.new_pipeline.label'),
      subtitle: t('crm.dashboard.actions.new_pipeline.subtitle'),
      color: 'var(--ant-color-success)',
      background: 'var(--ant-color-success-bg)',
      icon: Workflow,
    },
    {
      id: 'analytics',
      label: t('crm.dashboard.actions.analytics.label'),
      subtitle: t('crm.dashboard.actions.analytics.subtitle'),
      color: 'var(--ant-color-primary)',
      background: 'var(--ant-color-primary-bg)',
      icon: BarChart3,
    },
    {
      id: 'settings',
      label: t('crm.dashboard.actions.settings.label'),
      subtitle: t('crm.dashboard.actions.settings.subtitle'),
      color: 'var(--ant-color-text-secondary)',
      background: 'var(--g-fill)',
      icon: Settings,
    },
  ];
  const effectiveActions = actions ?? fallbackActions;

  const widgetContainerProps = {
    title: t('crm.dashboard.actions.title'),
    icon: Plus,
    minHeight: 200,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      <ul className="m-0 list-none p-0">
        {effectiveActions.map((action) => (
          <li key={action.id}>
            <button
              type="button"
              onClick={action.onClick}
              className="transition-colors text-left border-0 flex w-full items-center gap-3 crm-hover-fill"
              style={{
                cursor: action.onClick ? 'pointer' : 'default',
                opacity: action.onClick ? 1 : 0.65,
                padding: '4px 14px',
                minHeight: 42,
                borderBottom: '1px solid var(--ant-color-border-secondary)',
                background: 'transparent',
              }}
            >
              <div
                className="crm-radius-s flex items-center justify-center shrink-0"
                style={{ width: 30, height: 30, background: action.background }}
              >
                <action.icon size={15} style={{ color: action.color }} />
              </div>
              <div className="flex-1 min-w-0 py-1">
                <Text
                  strong
                  className="block"
                  style={{ fontSize: 12.5, color: 'var(--ant-color-text)', lineHeight: 1.2 }}
                >
                  {action.label}
                </Text>
                <Text type="secondary" className="block" style={{ fontSize: 11, marginTop: 1 }}>
                  {action.subtitle}
                </Text>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </WidgetContainer>
  );
}
